import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useI18n } from '../i18n'
import {
  useMe, useAdminOverview, useAdminYards, useAdminUsers, useAdminYard, useAdminUser,
} from '../api/hooks'
import { PageSpinner, Spinner, Modal } from '../components/ui'
import { fmtDate } from '../lib/format'

/**
 * Read-only support console. Reachable only when /me reports platformAdmin;
 * every endpoint behind it re-checks the caller's sub server-side, so hiding
 * the page is convenience, not the control.
 *
 * Shows state, never content: yards, accounts, membership, boarded horses by
 * name. No medical records, documents or photos — those stay between an owner
 * and the yard they shared them with.
 */
export function AdminConsole() {
  const { t, locale } = useI18n()
  const { data: me, isLoading: meLoading } = useMe()
  const [tab, setTab] = useState<'yards' | 'users'>('yards')
  const [openYard, setOpenYard] = useState<string | null>(null)
  const [openUser, setOpenUser] = useState<string | null>(null)

  const isAdmin = !!me?.platformAdmin
  const { data: ov } = useAdminOverview(isAdmin)
  const { data: yards, isLoading: yLoading } = useAdminYards(isAdmin && tab === 'yards')
  const { data: users, isLoading: uLoading } = useAdminUsers(isAdmin && tab === 'users')

  if (meLoading) return <PageSpinner />
  if (!isAdmin) return <Navigate to="/app" replace />

  return (
    <main className="mx-auto max-w-5xl px-5 py-8">
      <h1 className="text-2xl font-extrabold tracking-tight">{t('admin.title')}</h1>
      <p className="mt-1 text-sm text-brand-600 dark:text-brand-300">{t('admin.subtitle')}</p>

      {ov && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {([
            ['admin.stat.users', ov.users],
            ['admin.stat.yards', ov.yards],
            ['admin.stat.horses', ov.horses],
            ['admin.stat.boardings', ov.boardings],
            ['admin.stat.shares', ov.sharesGranted],
            ['admin.stat.invites', ov.pendingInvites],
          ] as const).map(([k, v]) => (
            <div key={k} className="card p-4">
              <div className="text-2xl font-extrabold">{v}</div>
              <div className="text-xs text-brand-500 dark:text-brand-400">{t(k)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 flex gap-2">
        {(['yards', 'users'] as const).map((k) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              tab === k ? 'bg-brand-700 text-white' : 'bg-white/70 dark:bg-brand-800/60 text-brand-700 dark:text-brand-200'
            }`}
          >
            {t(k === 'yards' ? 'admin.tab.yards' : 'admin.tab.users')}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {tab === 'yards' &&
          (yLoading ? (
            <div className="grid place-items-center py-8"><Spinner /></div>
          ) : (
            (yards ?? []).map((y) => (
              <button key={y.id} onClick={() => setOpenYard(y.id)}
                className="card flex w-full flex-wrap items-center justify-between gap-2 p-4 text-left transition hover:shadow-lift">
                <div className="min-w-0">
                  <div className="font-bold">🏡 {y.name}</div>
                  <div className="truncate text-xs text-brand-500 dark:text-brand-400">
                    {y.ownerName} · {y.ownerEmail}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="chip">{t('admin.members', { n: y.memberCount })}</span>
                  <span className="chip">{t('admin.horses', { n: y.horseCount })}</span>
                  <span className="chip">{y.plan}</span>
                </div>
              </button>
            ))
          ))}

        {tab === 'users' &&
          (uLoading ? (
            <div className="grid place-items-center py-8"><Spinner /></div>
          ) : (
            (users ?? []).map((u) => (
              <button key={u.userId} onClick={() => setOpenUser(u.userId)}
                className="card flex w-full flex-wrap items-center justify-between gap-2 p-4 text-left transition hover:shadow-lift">
                <div className="min-w-0">
                  <div className="font-bold">{u.displayName || u.email}</div>
                  <div className="truncate text-xs text-brand-500 dark:text-brand-400">{u.email}</div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {u.roles.farmOwner && <span className="chip">🏡</span>}
                  {u.roles.horseOwner && <span className="chip">🐎</span>}
                  <span className="chip">{t('admin.horses', { n: u.horseCount })}</span>
                  {!u.onboarded && <span className="chip !bg-amber-100 !text-amber-700 dark:!bg-amber-900/40 dark:!text-amber-300">{t('admin.notOnboarded')}</span>}
                </div>
              </button>
            ))
          ))}
      </div>

      {openYard && <YardModal id={openYard} onClose={() => setOpenYard(null)} locale={locale} />}
      {openUser && <UserModal id={openUser} onClose={() => setOpenUser(null)} />}
    </main>
  )
}

function YardModal({ id, onClose, locale }: { id: string; onClose: () => void; locale: string }) {
  const { t } = useI18n()
  const { data, isLoading } = useAdminYard(id)
  return (
    <Modal title={data?.yard.name || t('admin.tab.yards')} onClose={onClose}>
      {isLoading || !data ? (
        <div className="grid place-items-center py-8"><Spinner /></div>
      ) : (
        <div className="space-y-5 text-sm">
          <div>
            <div className="text-xs font-semibold text-brand-500 dark:text-brand-400">{t('admin.owner')}</div>
            <div>{data.yard.ownerName} · {data.yard.ownerEmail}</div>
            <div className="text-xs text-brand-500">
              {data.yard.plan} · {t('admin.created', { date: fmtDate(data.yard.createdAt, locale) })}
            </div>
          </div>
          <Block label={t('admin.people')} empty={t('admin.none')} rows={data.members.map((m) => `${m.name} (${m.role}) · ${m.email}`)} />
          <Block label={t('admin.boardedHorses')} empty={t('admin.none')}
            rows={data.horses.map((h) => `${h.horseName} — ${h.ownerName}${h.stableLocation ? ` · 📍 ${h.stableLocation}` : ''}`)} />
          <Block label={t('admin.invites')} empty={t('admin.none')}
            rows={data.invites.map((i) => `${i.email} (${i.role}) — ${i.status}`)} />
          <p className="text-xs text-brand-500 dark:text-brand-400">{t('admin.privacyNote')}</p>
        </div>
      )}
    </Modal>
  )
}

function UserModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useI18n()
  const { data, isLoading } = useAdminUser(id)
  return (
    <Modal title={data?.displayName || t('admin.tab.users')} onClose={onClose}>
      {isLoading || !data ? (
        <div className="grid place-items-center py-8"><Spinner /></div>
      ) : (
        <div className="space-y-5 text-sm">
          <div>
            <div>{data.email}</div>
            <div className="text-xs text-brand-500">
              {data.roles.farmOwner ? '🏡 ' : ''}{data.roles.horseOwner ? '🐎 ' : ''}
              {data.onboarded ? t('admin.onboarded') : t('admin.notOnboarded')}
            </div>
          </div>
          <Block label={t('admin.theirHorses')} empty={t('admin.none')}
            rows={data.horses.map((h) => `${h.name}${h.farmName ? ` — ${h.farmName}` : ''}`)} />
          <Block label={t('admin.ownedYards')} empty={t('admin.none')} rows={data.ownedYards.map((y) => y.name)} />
          <Block label={t('admin.memberOf')} empty={t('admin.none')}
            rows={data.memberOfYards.map((y) => `${y.name} (${y.role})`)} />
          <p className="text-xs text-brand-500 dark:text-brand-400">{t('admin.privacyNote')}</p>
        </div>
      )}
    </Modal>
  )
}

function Block({ label, rows, empty }: { label: string; rows: string[]; empty: string }) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-brand-500 dark:text-brand-400">{label}</div>
      {rows.length === 0 ? (
        <p className="text-brand-500 dark:text-brand-400">{empty}</p>
      ) : (
        <ul className="space-y-1">{rows.map((r, i) => <li key={i} className="truncate">{r}</li>)}</ul>
      )}
    </div>
  )
}
